import type pino from 'pino'

const timestamp = (): string => {
  const time = Date.now()

  return `,"time":${time},"timestamp":"${new Date(time).toString()}"`
}

const formatLevel = (label: string, number: number) => ({
  level: label,
  'log.level': number,
})

let pinoLogger: pino.Logger | null = null

/**
 * Get the Pino logger instance, creating it lazily on first access.
 *
 * This ensures the logger is created AFTER OpenTelemetry instrumentation
 * is registered, allowing PinoInstrumentation to inject trace context
 * (trace_id, span_id) into logs.
 *
 * Format log messages (using Pino) and sends to process.stdout.
 *
 * Please use the `logger()` function instead which incorporates the pinoLogger.
 *
 * pinoLogger.trace()
 * pinoLogger.debug()
 * pinoLogger.info()
 * pinoLogger.warn()
 * pinoLogger.error()
 * pinoLogger.fatal()
 *
 * Each logger function takes up to three parameters:
 * (https://getpino.io/#/docs/api?id=trace)
 * - ({ mergingObject }, "message", ...interpolationValues)
 * - ({ error: Error }, "message", ...interpolationValues)
 * - ({ message: "message" }, ...interpolationValues)
 * - (Error, "message", ...interpolationValues)
 * - ("message", ...interpolationValues)
 *
 * Examples:
 * pinoLogger.error(new Error('Blah blah'))
 * pinoLogger.error({ campaignId: 1 }, 'Blah blah')
 * pinoLogger.error({ message: 'Blah blach', campaignId: 1 })
 * pinoLogger.error('Hello %s %s', 'silly' 'world') -> "Hello silly world"
 */
export const getPinoLogger = (): pino.Logger => {
  if (!pinoLogger) {
    // Use require() to dynamically load pino AFTER instrumentation
    // This ensures PinoInstrumentation can patch the module
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const pinoModule = require('pino')

    pinoLogger = pinoModule({
      enabled: true,
      level: process.env.LOG_LEVEL || 'info',
      formatters: {
        level: formatLevel,
      },
      // Overriding Pino's "err" key name to "error"
      errorKey: 'error',
      // Overriding Pino's "msg" key name to "message"
      messageKey: 'message',
      timestamp,
    })
  }
  return pinoLogger!
}
